import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/types/api';

export type NotificationCategory =
  | 'EMERGENCY'
  | 'FINANCE'
  | 'SOCIETY'
  | 'SECURITY'
  | 'MEETINGS'
  | 'REQUESTS'
  | 'GOVERNANCE'
  | 'EVENTS'
  | 'SYSTEM'
  | 'AMENITIES';

export type NotificationPriority =
  | 'LOW'
  | 'NORMAL'
  | 'IMPORTANT'
  | 'HIGH'
  | 'CRITICAL';

export interface InboxNotification {
  _id: string;
  title: string;
  body: string;
  event: string;
  category?: NotificationCategory;
  priority?: NotificationPriority;
  entityType: string;
  entityId?: string | null;
  link?: string | null;
  readAt?: string | null;
  isImportant?: boolean;
  importantAt?: string | null;
  isEmergency?: boolean;
  acknowledgedAt?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, any> | null;
  createdAt?: string;
}

export interface UnreadCountResponse {
  count: number;
  total: number;
  unread: number;
  emergencyUnread: number;
  financeUnread: number;
  importantCount: number;
}

export interface NotificationListParams {
  page?: number;
  limit?: number;
  category?: string;
  unreadOnly?: boolean | string;
  importantOnly?: boolean | string;
  emergencyOnly?: boolean | string;
  priority?: string;
  search?: string;
}

export class NotificationService {
  static async list(params?: NotificationListParams) {
    const { data } = await apiClient.get<ApiResponse<InboxNotification[]>>('/notifications', {
      params,
    });
    return data;
  }

  static async unreadCount() {
    const { data } = await apiClient.get<ApiResponse<UnreadCountResponse>>(
      '/notifications/unread-count',
    );
    return data;
  }

  static async markRead(id: string) {
    const { data } = await apiClient.patch<ApiResponse<InboxNotification>>(
      `/notifications/${id}/read`,
    );
    return data;
  }

  static async toggleImportant(id: string) {
    const { data } = await apiClient.patch<ApiResponse<InboxNotification>>(
      `/notifications/${id}/important`,
    );
    return data;
  }

  static async acknowledgeEmergency(id: string) {
    const { data } = await apiClient.patch<ApiResponse<InboxNotification>>(
      `/notifications/${id}/acknowledge`,
    );
    return data;
  }

  static async markAllRead(category?: string) {
    const { data } = await apiClient.post<ApiResponse<{ updated: number }>>(
      '/notifications/read-all',
      { category },
    );
    return data;
  }

  static async clearAll() {
    const { data } = await apiClient.post<ApiResponse<{ cleared: number }>>(
      '/notifications/clear-all',
    );
    return data;
  }
}

